import React, {FC} from "react"

import {Body, H4, H6} from "@irati/ui/src/Typography"
import Flex from "@irati/ui/src/Flex"
import {formatCurrency} from "@irati/utils/currency/formatCurrency"
import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {RenewalOption} from "@irati/types"
import {CurrencyType} from "@irati/payments/enums"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {Tooltip} from "@irati/ui/src/Tooltip"
import {Hr} from "@irati/ui/src/Layout"
import Loading from "@irati/ui/src/Loading"

import {StyledCard, OuterCircle, PopularWrapper, Popular, RelativeIcon, RedStrikethrough, StyledIcon, AnnualSaving} from "./styled"
import MonthlyPrice from "./MonthlyPrice"
interface Props extends InjectIntl {
  load: Promise<void> | null
  quote: RenewalOption
  oneYearQuote?: RenewalOption
  isRecommended: boolean
  onUpdate: (q: RenewalOption) => void
  hideSelector: boolean
  isSelected: boolean
  currencyCode: CurrencyType
}

const RenewalCard: FC<Props> = ({
  quote,
  oneYearQuote,
  isRecommended,
  onUpdate,
  hideSelector,
  isSelected,
  currencyCode = CurrencyType.GBP,
  load,
  formatMessage
}) => {
  const theme = useTheme()
  const {termLength, annualSaving} = quote

  return (
    <StyledCard hideSelector={hideSelector} isSelected={isSelected} padding={2} onClick={() => onUpdate && onUpdate(quote)}>
      <Flex>
        <Flex>
          {!hideSelector && (
            <OuterCircle isSelected={isSelected}>{isSelected && <RelativeIcon icon={IconTypes.tick} color="white" />}</OuterCircle>
          )}
          <div>
            <Flex>
              <H4 marginBottom={1}>{`${termLength} ${formatMessage({
                id: "admin.billing.renewals.renewalCard.title"
              })}`}</H4>
              {!!annualSaving && (
                <Flex marginLeft={1} alignItems="flex-start">
                  <AnnualSaving small padding={0.5} bgColor={theme.colors.primary1}>
                    <Loading load={load}>
                      <H6 color={theme.colors.greys.white}>{`${formatMessage(
                        {
                          id: "admin.billing.renewals.renewalCard.annualSaving"
                        },
                        {
                          saving: formatCurrency({
                            currencyCode,
                            value: termLength === 24 ? annualSaving * 2 : annualSaving * 3
                          })
                        }
                      )}`}</H6>
                    </Loading>
                  </AnnualSaving>
                </Flex>
              )}
            </Flex>
          </div>
        </Flex>
      </Flex>
      {isRecommended && (
        <Tooltip placement="bottom" tooltipChildren={formatMessage({id: "admin.billing.renewals.renewalCard.tooltip.text"})}>
          <PopularWrapper>
            <Popular>
              <Flex paddingTop={0.5} direction="column" justifyContent="space-between" alignItems="center" paddingLeft={theme.spacing.two}>
                <Icon icon={IconTypes.starFill} color={theme.colors.primary2} />
                <Flex alignItems="center">
                  <H6 color={theme.colors.primary2}>
                    <FormattedMessage id="admin.billing.renewals.renewalCard.tooltip.popular" />
                  </H6>
                  <StyledIcon icon={IconTypes.circleAlertFill} size={theme.spacing.one} color={theme.colors.primary2} />
                </Flex>
              </Flex>
            </Popular>
          </PopularWrapper>
        </Tooltip>
      )}
      {hideSelector && (
        <div>
          {oneYearQuote?.costPerSeatMonth && oneYearQuote?.costPerSeatMonth !== quote?.costPerSeatMonth && (
            <Flex>
              <RedStrikethrough>
                <Body marginRight={1}>{formatCurrency({currencyCode, value: oneYearQuote?.costPerSeatMonth})}</Body>
              </RedStrikethrough>
              <Body weight="bold">{formatCurrency({currencyCode, value: quote?.costPerSeatMonth})}</Body>
              <Body small marginLeft>
                <FormattedMessage id="admin.billing.renewals.renewalCard.perUserMonthly" />
              </Body>
            </Flex>
          )}
          <Hr />
          <MonthlyPrice
            savingsCost={quote?.annualCost / 12}
            originalCost={oneYearQuote ? oneYearQuote?.annualCost / 12 : undefined}
            currencyCode={currencyCode}
          />
        </div>
      )}
    </StyledCard>
  )
}

export default injectIntl(RenewalCard)
